import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Star, Bell, Hourglass, PartyPopper } from "lucide-react";
import { useApp } from "../AppContext";
import api from "../api";
import NotifyMeModal from "./NotifyMeModal";
import { FlagStripe } from "./FlagAccents";

const MenuSection = () => {
    const { t, lang, addToCart, setCartOpen, fmtMoney, branding, apiBase } = useApp();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notifyItem, setNotifyItem] = useState(null);

    useEffect(() => {
        api.get(`${apiBase}/menu`)
            .then((r) => setItems(r.data || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, [apiBase]);

    const handleAdd = (item) => {
        if (!item.available) return;
        addToCart(item, { quantity: item.default_quantity || 1, pre_order: false });
        setCartOpen(true);
    };

    const handlePreorder = (item) => {
        addToCart(item, { quantity: item.default_quantity || 1, pre_order: true });
        setCartOpen(true);
    };

    return (
        <section
            id="menu"
            data-testid="menu-section"
            className="relative py-24 sm:py-32 bg-earth-50 grain"
        >
            {branding.decorations_enabled && (
                <FlagStripe thickness="h-1.5" className="absolute top-0 left-0 right-0 opacity-70" />
            )}
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                <div className="max-w-3xl mb-14">
                    <span className="pill-tag mb-5" data-testid="menu-tag">
                        {t.menu.tag}
                    </span>
                    <h2
                        data-testid="menu-title"
                        className="font-serif font-black text-earth-900 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
                    >
                        {t.menu.title}
                    </h2>
                    <p className="mt-5 text-lg text-earth-700 max-w-2xl">
                        {t.menu.subtitle}
                    </p>
                </div>

                {loading && (
                    <div className="text-earth-600" data-testid="menu-loading">
                        {t.menu.empty}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                    {items.map((item, idx) => {
                        const cateringOnly = item.catering_only === true;
                        const isAvailable = item.available !== false && !cateringOnly;
                        const preorderEnabled = item.preorder_enabled !== false && !cateringOnly;
                        const servingLabel =
                            lang === "es"
                                ? item.serving_size_es
                                : item.serving_size_en;
                        const defQty = item.default_quantity || 1;
                        const goToCatering = () => {
                            document
                                .getElementById("catering")
                                ?.scrollIntoView({ behavior: "smooth", block: "start" });
                        };
                        return (
                            <motion.article
                                key={item.id}
                                data-testid={`menu-item-${item.slug}`}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.5, delay: idx * 0.06 }}
                                className={`food-card group relative flex flex-col overflow-hidden rounded-3xl bg-white border border-earth-200 shadow-sm transition-all duration-500 ${
                                    isAvailable
                                        ? "hover:shadow-xl hover:-translate-y-1"
                                        : "opacity-90"
                                }`}
                            >
                                <div className="relative aspect-[4/3] overflow-hidden bg-earth-100">
                                    <img
                                        src={item.image_url}
                                        alt={lang === "es" ? item.name_es : item.name_en}
                                        className={`food-card-image w-full h-full object-cover ${
                                            !isAvailable ? "grayscale-[40%]" : ""
                                        }`}
                                        loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-earth-900/30 via-transparent to-transparent" />

                                    {item.featured && isAvailable && (
                                        <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-terracotta-500 text-white text-[10px] font-bold uppercase tracking-wider shadow-md">
                                            <Star className="w-3 h-3 fill-current" />
                                            {t.menu.featured}
                                        </div>
                                    )}

                                    {cateringOnly ? (
                                        <div
                                            data-testid={`catering-only-${item.slug}`}
                                            className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-md"
                                            style={{ background: "#7c3aed", color: "#ffffff" }}
                                        >
                                            <PartyPopper className="w-3 h-3" />
                                            {lang === "es" ? "Solo catering" : "Catering only"}
                                        </div>
                                    ) : !isAvailable ? (
                                        <div
                                            data-testid={`coming-soon-${item.slug}`}
                                            className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-md"
                                            style={{ background: "#002D62", color: "#FFFFFF" }}
                                        >
                                            <Hourglass className="w-3 h-3" />
                                            {lang === "es" ? "Pre-orden 72h" : "72h pre-order"}
                                        </div>
                                    ) : null}
                                </div>
                                <div className="p-6 flex flex-col flex-1">
                                    <div className="flex items-start justify-between gap-4 mb-1">
                                        <h3 className="font-serif text-2xl font-bold text-earth-900 leading-tight">
                                            {lang === "es" ? item.name_es : item.name_en}
                                        </h3>
                                        <div className="text-right shrink-0">
                                            <div className="text-[10px] uppercase tracking-wider text-earth-500 font-semibold">
                                                {t.menu.from}
                                            </div>
                                            <div className="font-serif font-bold text-terracotta-600 text-2xl">
                                                {fmtMoney(item.price)}
                                            </div>
                                        </div>
                                    </div>

                                    {servingLabel && (
                                        <div
                                            data-testid={`serving-${item.slug}`}
                                            className="text-[11px] uppercase tracking-wider font-bold text-leaf-600 mb-3"
                                        >
                                            🍽 {servingLabel}
                                            {defQty > 1 && (
                                                <span className="text-earth-500 normal-case font-medium tracking-normal">
                                                    {" "}
                                                    · {lang === "es" ? "estándar" : "default"}: {defQty}
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    <p className="text-sm text-earth-700 leading-relaxed mb-5">
                                        {lang === "es"
                                            ? item.description_es
                                            : item.description_en}
                                    </p>
                                    {cateringOnly ? (
                                        <button
                                            data-testid={`catering-cta-${item.slug}-btn`}
                                            onClick={goToCatering}
                                            className="mt-auto inline-flex items-center justify-center gap-2 rounded-full text-white py-3 px-5 font-semibold text-sm transition-all duration-300 shadow-sm hover:shadow-md"
                                            style={{ background: "#7c3aed" }}
                                            title={
                                                lang === "es"
                                                    ? "Este plato se sirve solo para catering. Pide tu cotización personalizada."
                                                    : "This dish is available for catering only. Request your custom quote."
                                            }
                                        >
                                            <PartyPopper className="w-4 h-4" />
                                            {lang === "es" ? "Cotizar para catering" : "Quote for catering"}
                                        </button>
                                    ) : isAvailable ? (
                                        <button
                                            data-testid={`add-to-cart-${item.slug}-btn`}
                                            onClick={() => handleAdd(item)}
                                            className="mt-auto inline-flex items-center justify-center gap-2 rounded-full bg-earth-900 hover:bg-terracotta-500 text-white py-3 px-5 font-semibold text-sm transition-all duration-300 group/btn"
                                        >
                                            <Plus className="w-4 h-4 group-hover/btn:rotate-90 transition-transform" />
                                            {defQty > 1
                                                ? `${t.menu.add} ${defQty}×`
                                                : t.menu.add}
                                        </button>
                                    ) : (
                                        <div className="mt-auto grid grid-cols-2 gap-2">
                                            <button
                                                data-testid={`notify-me-${item.slug}-btn`}
                                                onClick={() => setNotifyItem(item)}
                                                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-white border-2 border-earth-900 text-earth-900 hover:bg-earth-900 hover:text-white py-2.5 px-3 font-semibold text-[12px] transition-all duration-300"
                                                title={
                                                    lang === "es"
                                                        ? "Te avisamos por WhatsApp cuando este plato esté listo en el menú."
                                                        : "We'll notify you on WhatsApp when this dish is back on the menu."
                                                }
                                            >
                                                <Bell className="w-3.5 h-3.5" />
                                                {lang === "es" ? "Avísame" : "Notify me"}
                                            </button>
                                            {preorderEnabled ? (
                                                <button
                                                    data-testid={`preorder-${item.slug}-btn`}
                                                    onClick={() => handlePreorder(item)}
                                                    className="group/po relative inline-flex items-center justify-center gap-1.5 rounded-full text-white py-2.5 px-3 font-semibold text-[12px] transition-all duration-300 shadow-sm hover:shadow-md"
                                                    style={{ background: "#002D62" }}
                                                    title={
                                                        lang === "es"
                                                            ? "Pre-ordena con 72 horas de anticipación. Te confirmamos la fecha exacta de entrega por WhatsApp."
                                                            : "Pre-order with 72 hours notice. We'll confirm the exact delivery date via WhatsApp."
                                                    }
                                                >
                                                    <Hourglass className="w-3.5 h-3.5" />
                                                    {lang === "es" ? "Pre-orden 72h" : "Pre-order 72h"}
                                                    {/* Custom tooltip on hover (desktop) */}
                                                    <span
                                                        role="tooltip"
                                                        className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-xl bg-earth-900 text-white text-[11px] font-normal leading-snug px-3 py-2.5 shadow-xl opacity-0 group-hover/po:opacity-100 transition-opacity duration-200 z-20"
                                                    >
                                                        {lang === "es" ? (
                                                            <>
                                                                ⏳ <b>Pre-orden con 72 horas de anticipación.</b>
                                                                <br />
                                                                Recibimos tu pedido hoy y te confirmamos
                                                                fecha exacta de entrega por WhatsApp.
                                                            </>
                                                        ) : (
                                                            <>
                                                                ⏳ <b>Pre-order with 72 hours notice.</b>
                                                                <br />
                                                                We'll receive your order today and confirm
                                                                the exact delivery date via WhatsApp.
                                                            </>
                                                        )}
                                                        <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-earth-900" />
                                                    </span>
                                                </button>
                                            ) : null}
                                        </div>
                                    )}
                                </div>
                            </motion.article>
                        );
                    })}
                </div>
            </div>
            <NotifyMeModal item={notifyItem} onClose={() => setNotifyItem(null)} />
        </section>
    );
};

export default MenuSection;
